import { Injectable, inject, OnDestroy, computed, signal } from "@angular/core";
import {
    CurrentSection,
    ItemsEstended,
    PromotionsDefault,
    PromotionsFields,
    PromotionsForm,
    PromotionsSearchItemsForm,
    PromotionType,
    SearchFilter,
    StatusItemsSource,
    TableItemsPromotion
} from '../components';
import { TranslateService } from "@ngx-translate/core";
import { PromotionsFe } from "src/app/shared/model/model";
import moment from 'moment-timezone';
import {
    Categories,
    ChainPromotions,
    Channels,
    Items,
    PromotionsItems,
    Suppliers
} from "tilby-models";
import { DevLogger } from "src/app/shared/dev-logger";
import {
    ConfigurationManagerService,
    EntityManagerService,
    ErrorsLoggerService,
    UserActiveSessionManagerService
} from "src/app/core";
import { restManager } from "app/ajs-upgraded-providers";
import { BehaviorSubject, Subject } from "rxjs";
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { CustomFormControl, CustomFormControlProps, CustomFormGroup, KeyValueIconImage } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { ShopChain } from "src/app/models";
import { KeyValue } from "@angular/common";
import { Validators } from "@angular/forms";
import { keyBy, REGEX_TIME_VALIDATOR } from "src/app/shared/utils";

type WeekDaysMap = {
    [key: string]: string; // Keys are strings, values are strings (day mappings)
};

@Injectable({
    providedIn: 'root'
})
export class UtilsPromotionsService implements OnDestroy {
    private readonly configurationManager = inject(ConfigurationManagerService);
    private readonly entityManager = inject(EntityManagerService);
    private readonly restManagerService = inject(restManager);
    private readonly userActiveSession = inject(UserActiveSessionManagerService);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly translate = inject(TranslateService);
    private readonly onDestroy$ = new Subject<void>();
    private readonly errorsLoggerService = inject(ErrorsLoggerService);

    private statusSource = new BehaviorSubject<StatusItemsSource>({
        itemsSourceAdd: [],
        itemsSourceRemoved: [],
        requiredItemsSourceAdd: [],
        requiredItemsSourceRemoved: []
    });
    currentStatusSource$ = this.statusSource.asObservable();

    promotionForm!: CustomFormGroup<PromotionsForm>;
    formSearchItems!:CustomFormGroup<PromotionsSearchItemsForm>;
    itemsSourceAdd: Array<ItemsEstended> = [];
    itemsSourceRemoved: Array<ItemsEstended> = [];
    requiredItemsSourceAdd: Array<ItemsEstended> = [];
    requiredItemsSourceRemoved: Array<ItemsEstended> = [];
    $promotion_type = signal<PromotionType>('discount_perc');
    $perc_discount = signal<number>(0);

    itemsBySku: Record<string, ItemsEstended> = {};
    selectedItems: Array<ItemsEstended> = [];
    requiredItems: Array<ItemsEstended> = [];
    availableItems: Array<ItemsEstended> = [];
    availableReqItems: Array<ItemsEstended> = [];
    items: Array<Items> = [];
    itemsWithSku: Array<ItemsEstended> = [];
    brands: Array<string> = [];

    currentPromotion: PromotionsFields = new PromotionsDefault();
    weekDays: Array<{id: string, name: string}> = [];
    weekDaysChoices: Array<KeyValue<string, number>> = [];
    chainShops: Array<ShopChain> = [];
    priceLists = [
        {
            index: -1,
            fieldName: this.translate.instant('PROMOTIONS.DETAILS.BASE_PRICELIST'),
            value: this.translate.instant('PROMOTIONS.DETAILS.BASE_PRICELIST'),
            hidden: false
        },
        ...Array.from({ length: 10 }, (_, i) => ({
            index: (i + 1),
            fieldName: `price${i+1}`,
            value: this.configurationManager.getPreference(`price_list_${i+1}_name`) || `${this.translate.instant('PROMOTIONS.DETAILS.PRICE_LIST_START')} ${i+1}`,
            hidden: !!this.configurationManager.getPreference(`price_list_${i+1}_hide`)
        }))
    ];
    private currentShopChain?: ShopChain;
    user = this.userActiveSession.getSession()!;

    //weekDaysMap: any = _.zipObject(moment.localeData('en').weekdaysMin(), _.map(moment.weekdaysShort(false), _.upperFirst));
    weekDaysMin = moment.localeData('en').weekdaysMin();
    weekdaysShort = moment.weekdaysShort(false).map(day => day.charAt(0).toUpperCase() + day.slice(1));

    weekDaysMap = this.weekDaysMin.reduce<WeekDaysMap>((acc, day, index) => {
        acc[day] = this.weekdaysShort[index];
        return acc;
    }, {});

    public currentSection = signal<CurrentSection>('general');
    public showConfirm = computed(() => this.currentSection() !== 'required-items');
    public showTabs = computed(() => this.currentSection() !== 'required-items');
    searchFilter: SearchFilter = {
        text: undefined,
        supplierId: undefined,
        brand: undefined,
        categoryId: undefined,
        channel: undefined
    };

    constructor() {
        this.log('constructor');
        this.log('currentSection', this.currentSection());
        this.log('showConfirm', this.showConfirm());
        this.log('showTabs', this.showTabs());
        this.weekDays = moment.localeData('en').weekdaysMin().map((id, index) => ({
            id,
            name: moment.weekdays(false)[index].charAt(0).toUpperCase() + moment.weekdays(false)[index].slice(1)
        }));
        this.weekDaysChoices = this.weekDays.map((weekDay, i) => ({ key: weekDay.name, value: Number(weekDay.id) || i}));
        // this.log("weekDays", this.weekDays);
        // this.log("weekDaysChoices", this.weekDaysChoices);

        // this.log("weekDaysMin", this.weekDaysMin);
        // this.log("weekdaysShort", this.weekdaysShort);
        this.init();
    }

    async init() {
        this.log('init');

        const uniqueBrands = new Set<string>();

        this.items = await this.getRemoteItems();
        this.log('--- items', this.items);
        this.itemsWithSku = this.items.filter(item => item.sku).map((item: ItemsEstended) => {
            item._enabled = false;
            item._required = false;

            // Get brands
            const brand = item?.brand?.trim();
            if (brand) {
                uniqueBrands.add(brand);
            }

            return item;
        });
        this.log('--- itemsWithSku', this.itemsWithSku);

        this.brands = Array.from(uniqueBrands);
        this.log('--- brands', this.brands);

        this.itemsBySku = keyBy(this.itemsWithSku, (item) => item.sku);

        this.chainShops = await this.getRemoteChainShops();
        this.log("REMOTE chainShops", this.chainShops);
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    async getRemoteItems() {
        try {
            const items = await this.entityManager.items.fetchCollectionOffline();
            return (items && Array.isArray(items)) ? items : [];
        } catch (e) {
            this.errorsLoggerService.err(e);
            return [];
        }
    }

    getItemsWithSku() {
        if(this.items && this.items.length > 0) {
            const itemsWithSku = this.items.filter(item => item.sku);
            this.log('getItemsWithSku -> itemsWithSku', itemsWithSku);
            return itemsWithSku && itemsWithSku.length > 0 ? itemsWithSku : [];
        } else {
            return [];
        }
    }

    async getRemoteChainShops() {
        try {
            const chain_shops = await this.restManagerService.getList("chain_shops", {pagination: false});
            return (chain_shops && Array.isArray(chain_shops)) ? chain_shops : [];
        } catch (e) {
            this.errorsLoggerService.err(e);
            return [];
        }
    }

    /** FORM DETAILS */
    async createFormDetails(promotion: PromotionsFields = new PromotionsDefault(), channels: Array<Channels>, hiddenFields?:Array<keyof PromotionsFields>) {
        this.log("createFormDetails params promotion", promotion);
        this.currentPromotion = promotion;
        this.log("createFormDetails params channels", channels);
        await this.init();
        await this.parsePromotionsData(promotion);
        let chainShopsChoices : Array<KeyValue<string, string>> = [];
        if(this.chainShops && this.chainShops.length > 0) {
            this.currentShopChain = this.chainShops.find(shop => shop.db_name === this.user.shop.name);
            this.log('CURRENT_SHOP_CHAIN', this.currentShopChain, this.chainShops, this.user.shop);
            chainShopsChoices = this.chainShops.map(chainShop => ({
                key: chainShop.shop_name ? `${chainShop.shop_name} (${chainShop.db_name})` : `(${chainShop.db_name})`,
                value: chainShop.db_name
            })) || [];
        }

        const applyRuleTrue = this.translate.instant('PROMOTIONS.DETAILS.REQUIRE_ALL_ITEMS_TRUE');
        const applyRuleFalse = this.translate.instant('PROMOTIONS.DETAILS.REQUIRE_ALL_ITEMS_FALSE');
        const none = this.translate.instant('PROMOTIONS.DETAILS.NONE');
        const only_fidelity = this.translate.instant('PROMOTIONS.DETAILS.ONLY_FIDELITY');
        const no_fidelity = this.translate.instant('PROMOTIONS.DETAILS.NO_FIDELITY');
        const normal = this.translate.instant('PROMOTIONS.DETAILS.ORDER_TYPE_NORMAL');
        const take_away = this.translate.instant('PROMOTIONS.DETAILS.ORDER_TYPE_TAKE_AWAY');
        const delivery = this.translate.instant('PROMOTIONS.DETAILS.ORDER_TYPE_DELIVERY');
        const customerTypes = this.configurationManager.getPreference('customers.custom_types')?.split('\n') || [];
        const customerTypesChoices = customerTypes?.map(customerType => ({ key: customerType, value: customerType})) || [];
        const channelsChoices = channels?.map(channel => ({ key: channel.name, value: channel.id})) || [];

        const promotionType: PromotionType = promotion.perc_discount ? 'discount_perc' : 'discount_fix';
        this.$promotion_type.set(promotionType);
        const discount_perc = this.translate.instant('PROMOTIONS.DETAILS.TYPE_DISCOUNT_PERC');
        const discount_fix = this.translate.instant('PROMOTIONS.DETAILS.TYPE_DISCOUNT_FIX');

        const priceListsChoices: Array<KeyValue<string, number>> = this.priceLists.filter(priceList => !priceList.hidden).map(priceList => ({key: priceList.value, value: priceList.index}));

        this.promotionForm = new CustomFormGroup<PromotionsForm>({
            active: new CustomFormControl(promotion.active || false, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.ENABLED',
                inputType: 'slideToggle',
                class: 'tw-w-full tw-p-4 tw-pr-4 tw-mt-2 tw-mb-2'
            }),
            name: new CustomFormControl(promotion.name || '', { validators: Validators.required }, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.NAME',
                inputType: 'text',
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            from_date: new CustomFormControl(promotion.from_date, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.FROM',
                inputType: 'date',
                class: 'tw-w-[45%] tw-pl-4 tw-pr-4 sm:tw-pr-0 tw-mb-2'
            }),
            to_date: new CustomFormControl(promotion.to_date, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.TO',
                inputType: 'date',
                class: 'tw-w-[45%] tw-pl-4 sm:tw-pl-0 tw-pr-4 tw-mb-2',
                inputConstraint: { minDate: promotion.from_date ? promotion.from_date : this.from_date?.value }
            }),
            start_time: new CustomFormControl(this.removeSeconds(promotion.start_time || '00:00') , { validators: [Validators.required, Validators.pattern(REGEX_TIME_VALIDATOR)] }, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.START_TIME',
                inputType: 'time',
                class: 'tw-w-[45%] tw-pl-4 tw-pr-4 sm:tw-pr-0 tw-mb-2'
            }),
            end_time: new CustomFormControl(this.removeSeconds(promotion.end_time || '23:59'), { validators: [Validators.required, Validators.pattern(REGEX_TIME_VALIDATOR)] }, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.END_TIME',
                inputType: 'time',
                class: 'tw-w-[45%] tw-pl-4 sm:tw-pl-0 tw-pr-4 tw-mb-2'
            }),
            weekdays_period: new CustomFormControl(this.getNumbersOfWeekdays(promotion.weekdays_period || []), {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.WEEKDAYS_PERIOD',
                inputType: 'selectMultiple',
                inputChoices: this.weekDaysChoices,
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            required_items: new CustomFormControl(promotion.required_items?.length || 0, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.REQUIRED_ITEMS',
                inputType: 'text',
                id: 'required_items_controller',
                class: 'tw-w-[45%] tw-pl-4 tw-pr-4 sm:tw-pr-0 tw-mb-2 tw-cursor-pointer',
                matElementClass: 'tw-cursor-pointer',
            }),
            require_all_items: new CustomFormControl(promotion.require_all_items ? 'true' : 'false', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.REQUIRE_ALL_ITEMS',
                inputType: 'select',
                inputChoices: [
                    { key: applyRuleTrue, value: 'true' },
                    { key: applyRuleFalse, value: 'false' }
                ],
                class: 'tw-w-[45%] tw-pl-4 sm:tw-pl-0 tw-pr-4 tw-mb-2'
            }),
            fidelity_rule: new CustomFormControl(promotion.fidelity_rule || 'none', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.FIDELITY_RULE',
                inputType: 'select',
                inputChoices: [
                    { key: none, value: 'none' },
                    { key: only_fidelity, value: 'only_fidelity' },
                    { key: no_fidelity, value: 'no_fidelity' }
                ],
                class: 'tw-w-[45%] tw-pl-4 tw-pr-4 sm:tw-pr-0 tw-mb-2'
            }),
            customer_type: new CustomFormControl(promotion.customer_type || 'null', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.CUSTOMER_TYPE',
                inputType: 'select',
                inputChoices: [
                    { key: none, value: 'null' },
                    ...customerTypesChoices
                ],
                class: 'tw-w-[45%] tw-pl-4 sm:tw-pl-0 tw-pr-4 tw-mb-2'
            }),
            channels: new CustomFormControl(promotion.channels || [], {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.CHANNELS',
                inputType: 'selectMultiple',
                inputChoices: channelsChoices,
                class: 'tw-w-[45%] tw-pl-4 tw-pr-4 sm:tw-pr-0 tw-mb-2'
            }),
            shops: new CustomFormControl(promotion.shops || [], {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.SHOPS',
                inputType: 'selectMultiple',
                inputChoices: chainShopsChoices,
                class: 'tw-w-[45%] tw-pl-4 sm:tw-pl-0 tw-pr-4 tw-mb-2'
            }),
            order_types: new CustomFormControl(promotion.order_types || [], {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.ORDER_TYPE',
                inputType: 'selectMultiple',
                inputChoices: [
                    { key: normal, value: 'normal' },
                    { key: take_away, value: 'take_away' },
                    { key: delivery, value: 'delivery' }
                ],
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            promotion_type: new CustomFormControl(promotionType || 'discount_perc', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.PROMOTION_TYPE',
                inputType: 'select',
                inputChoices: [
                    { key: discount_perc, value: 'discount_perc' },
                    { key: discount_fix, value: 'discount_fix' }
                ],
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            perc_discount: new CustomFormControl(promotion.perc_discount || 0, {validators: [Validators.min(0), Validators.max(100)]}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.PERC_DISCOUNT',
                inputType: 'number',
                inputTypeInitial: promotionType === 'discount_fix' ? 'hidden' : 'number',
                inputConstraint: { min: 0, max: 100 },
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            base_pricelist: new CustomFormControl(promotion.base_pricelist || 1, {validators: Validators.required}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.BASE_PRICELIST',
                inputType: 'select',
                inputTypeInitial: promotionType === 'discount_perc' ? 'hidden' : 'select',
                inputChoices: priceListsChoices,
                class: 'tw-w-[45%] tw-pl-4 tw-pr-4 sm:tw-pr-0 tw-mb-2'
            }),
            quantity_threshold: new CustomFormControl(promotion.quantity_threshold || 1, {validators: [Validators.required,Validators.min(1)]}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.QUANTITY_THRESHOLD',
                inputType: 'number',
                inputTypeInitial: promotionType === 'discount_perc' ? 'hidden' : 'number',
                inputConstraint: { min: 1 },
                class: 'tw-w-[45%] tw-pl-4 sm:tw-pl-0 tw-pr-4 tw-mb-2'
            }),
            apply_below_threshold: new CustomFormControl(promotion.apply_below_threshold, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.APPLY_BELOW_THRESHOLD',
                inputType: 'checkbox',
                inputTypeInitial: promotionType === 'discount_perc' ? 'hidden' : 'checkbox',
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            multiples_only: new CustomFormControl(promotion.multiples_only, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.MULTIPLES_ONLY',
                inputType: 'checkbox',
                inputTypeInitial: promotionType === 'discount_perc' ? 'hidden' : 'checkbox',
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            force_pricelist_apply: new CustomFormControl(promotion.force_pricelist_apply, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.FORCE_PRICELIST_APPLY',
                inputType: 'checkbox',
                inputTypeInitial: promotionType === 'discount_perc' ? 'hidden' : 'checkbox',
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            }),
            exclude_other_promotions: new CustomFormControl(promotion.exclude_other_promotions, {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.EXCLUDE_OTHER_PROMOTIONS',
                inputType: 'checkbox',
                class: 'tw-w-full tw-pl-4 tw-pr-4 tw-mb-2'
            })
        });

        return this.promotionForm;
    }

    /** FORM SEARCH ITEMS */
    createFormSearchItems(categories: Array<Categories>, suppliers: Array<Suppliers>, channels: Array<Channels>) {
        const brandChoices: KeyValueIconImage<string>[] = this.brands ? this.brands.map((brand) => ({ key: brand, value: brand })) : [];
        const categoriesChoices: KeyValueIconImage<string>[] = categories.map((category: Categories) => ({
            key: category.name,
            value: category.name
        }));
        const suppliersChoices: KeyValueIconImage<string>[] = suppliers.map((supplier: Suppliers) => ({
            key: supplier.name,
            value: supplier.name
        }));
        const channelsChoices: KeyValueIconImage<string>[] = channels.map((channel: Channels) => ({
            key: channel?.id,
            value: channel.name
        }))
        this.formSearchItems = new CustomFormGroup<PromotionsSearchItemsForm>({
            search: new CustomFormControl('', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.SEARCH',
                inputType: 'text',
                customActions: { suffix: { icon: 'close', callback: () => this.resetSearch() } },
                class: 'lg:tw-w-full'
            }),
            supplier: new CustomFormControl('', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.SUPPLIER',
                inputType: 'autocomplete',
                inputChoices: suppliersChoices,
                customActions: { suffix: { icon: 'close', callback: () => this.resetSupplier() } }
            }),
            brand: new CustomFormControl('', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.BRAND',
                inputType: 'autocomplete',
                inputChoices: brandChoices,
                customActions: { suffix: { icon: 'close', callback: () => this.resetBrand() } }
            }),
            category: new CustomFormControl('', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.CATEGORY',
                inputType: 'autocomplete',
                inputChoices: categoriesChoices,
                customActions: { suffix: { icon: 'close', callback: () => this.resetCategory() } }
            }),
            channel: new CustomFormControl('', {}, {
                ...new CustomFormControlProps(),
                hint: () => ``,
                label: 'PROMOTIONS.DETAILS.CHANNEL',
                inputType: 'autocomplete',
                inputChoices: channelsChoices,
                customActions: { suffix: { icon: 'close', callback: () => this.resetChannel() } }
            })
        });

        return this.formSearchItems;
    }

    resetSearch() {
        this.formSearchItems.controls.search.setValue('');
    }

    resetSupplier() {
        this.formSearchItems.controls.supplier.setValue('');
    }

    resetBrand() {
        this.formSearchItems.controls.brand.setValue('');
    }

    resetCategory() {
        this.formSearchItems.controls.category.setValue('');
    }

    resetChannel() {
        this.formSearchItems.controls.channel.setValue('');
    }

    setSearchFilterCategoryId(categoryId: number | undefined) {
        this.searchFilter.categoryId = categoryId;
        this.applyFilters();
    }

    setSearchFilterChannel(channelName: string) {
        const lowerCaseChannelName = channelName?.toLowerCase() || undefined;
        this.searchFilter.channel = lowerCaseChannelName;
        this.applyFilters();
    }

    setSearchFilterText(textSearch: string) {
        const searchTerm = textSearch?.trim().toLowerCase() || undefined;
        this.searchFilter.text = searchTerm;
        this.applyFilters();
    }

    setSearchFilterSupplierId(supplierId: number | undefined) {
        this.searchFilter.supplierId = supplierId;
        this.applyFilters();
    }

    setSearchFilterBrand(brand: string) {
        this.searchFilter.brand = brand || undefined;
        this.applyFilters();
    }

    resetFilters() {
        this.searchFilter = {
            text: undefined,
            supplierId: undefined,
            brand: undefined,
            categoryId: undefined,
            channel: undefined
        };

        this.applyFilters();
    }

    applyFilters() {
        switch(this.currentSection()) {
            case 'items':
                this.updateAvailableItems();
            case 'required-items':
                this.updateAvailableReqItems();
        }

        this.applyStatusSource();
    }

    applyStatusSource() {
        const statusSource =  {
            itemsSourceAdd: this.itemsSourceAdd,
            itemsSourceRemoved: this.itemsSourceRemoved,
            requiredItemsSourceAdd: this.requiredItemsSourceAdd,
            requiredItemsSourceRemoved: this.requiredItemsSourceRemoved
        };

        this.statusSource.next(statusSource);
    }

    async parsePromotionsData(promotion: PromotionsFields) {
        this.selectedItems = [];
        this.requiredItems = [];

        promotion.items?.forEach((item: PromotionsItems) => {
            const refItem = this.itemsBySku[item.item_sku];

            if(refItem) {
                refItem._enabled = true;
                refItem._prevId = item.id;
                refItem._discountedPrice = item.discounted_price;

                this.selectedItems.push(refItem);
            }
        });

        this.log('parsePromotionsData selectedItems', this.selectedItems);
        this.itemsSourceRemoved = this.selectedItems;

        promotion.required_items?.forEach((item: PromotionsItems) => {
            const refItem = this.itemsBySku[item.item_sku];

            if(refItem) {
                refItem._required = true;
                refItem._prevReqId = item.id;
                refItem._quantity = item.quantity;

                this.requiredItems.push(refItem);
            }
        });

        this.log('parsePromotionsData requiredItems', this.requiredItems);
        this.requiredItemsSourceRemoved = this.requiredItems;
    }

    private applyItemFilters(item: ItemsEstended, filter: SearchFilter) {
        if(filter.supplierId && item.default_supplier_id !== filter.supplierId) {
            return false;
        }

        if(filter.categoryId && item.category_id !== filter.categoryId) {
            return false;
        }

        if(filter.brand && item.brand !== filter.brand) {
            return false;
        }

        if(filter.channel && !item.channels?.some(c => c.channel_id.toLowerCase() === filter.channel)) {
            return false;
        }

        return true;
    }

    private filterAvailableItems(filter: SearchFilter, keyField: keyof ItemsEstended) {
        let filteredItems = this.itemsWithSku.filter((item: ItemsEstended) => {
            if(item[keyField]) {
                return false;
            }

            return this.applyItemFilters(item, filter);
        });

        let filterText = filter.text;

        if(filterText) {
            if(filterText.startsWith('*')) {
                filterText = filterText.substring(1);
                filteredItems = filteredItems.filter((item: ItemsEstended) => item.name.toLowerCase().endsWith(filterText!));
            } else if(filterText.endsWith('*')) {
                filterText = filterText.slice(0, -1);
                filteredItems = filteredItems.filter((item: ItemsEstended) => item.name.toLowerCase().startsWith(filterText!));
            } else {
                filteredItems = filteredItems.filter((item: ItemsEstended) => item.name && item.name.toLowerCase().includes(filterText!));
            }
        }

        return filteredItems;
    }

    updateAvailableItems() {
        //this.log('updateAvailableItems');
        this.availableItems = this.filterAvailableItems(this.searchFilter, '_enabled');
        this.itemsSourceAdd = this.availableItems;
        this.log('this.itemsWithSku', this.itemsWithSku.length);
        this.log('foundAvailableItems', this.availableItems.length);
    }

    updateAvailableReqItems() {
        //this.log('updateAvailableReqItems');
        this.availableReqItems = this.filterAvailableItems(this.searchFilter, '_required');
        this.requiredItemsSourceAdd = this.availableReqItems;
        this.log('this.itemsWithSku', this.itemsWithSku.length);
        this.log('foundAvailableReqItems', this.availableReqItems.length);
    }

    removeAll() {
        //this.log('removeAll');
        switch(this.currentSection()) {
            case 'required-items':
                this.removeAllRequiredItems();
            case 'items':
                this.removeAllItems();
        }

        this.applyStatusSource();
    }

    removeAllItems() {
        //this.log('removeAllItems');
        this.selectedItems.forEach((item: ItemsEstended) => {
            item._enabled = false;
            item._discountedPrice = null;
        });

        this.selectedItems = [];
        this.itemsSourceRemoved = this.selectedItems;
        this.updateAvailableItems();
    }

    removeAllRequiredItems() {
        //this.log('removeAllRequiredItems');
        this.requiredItems.forEach((item: ItemsEstended) => {
            item._required = false;
            item._quantity = null;
        });

        this.requiredItems = [];
        this.requiredItemsSourceRemoved = this.requiredItems;
        this.updateAvailableReqItems();
    }

    addAll() {
        // this.log('addAll');
        switch(this.currentSection()) {
            case 'required-items':
                this.addAllRequiredItems();
            case 'items':
                this.addAllItems();
        }

        this.applyStatusSource();
    }

    addAllItems() {
        //this.log('addAllItems');
        this.availableItems.forEach((item: ItemsEstended) => {
            item._enabled = true;
            item._discountedPrice = null;

            this.selectedItems.push(item);
        });

        this.itemsSourceRemoved = this.selectedItems;
        this.updateAvailableItems();
    }

    addAllRequiredItems() {
        //this.log('addAllRequiredItems');
        this.availableReqItems.forEach((item: ItemsEstended) => {
            item._required = true;
            item._quantity = item._quantity == null ? 1 : item._quantity;

            this.requiredItems.push(item);
        });

        this.itemsSourceRemoved = this.requiredItems;
        this.updateAvailableReqItems();
    }

    addRow(row: TableItemsPromotion) {
        //this.log('addRow', row);
        const foundItem = this.itemsBySku[row.sku];

        if(foundItem) {
            switch(this.currentSection()) {
                case 'required-items':
                    this.addRequiredItem(foundItem);
                case 'items':
                    this.addItem(foundItem);
            }
        }

        this.applyStatusSource();
    }

    addRequiredItem(item: ItemsEstended) {
        //this.log('addRequiredItem', item);

        if(!item._required) {
            item._required = true;
            item._quantity = item._quantity == null ? 1 : item._quantity;

            this.requiredItems.push(item);
            this.requiredItemsSourceRemoved = this.requiredItems;
            this.updateAvailableReqItems();
        }
    }

    addItem(item: ItemsEstended) {
        //this.log('addItem', item);

        if(!item._enabled) {
            item._enabled = true;
            item._discountedPrice = null;

            this.selectedItems.push(item);
            this.itemsSourceRemoved = this.selectedItems;
            this.updateAvailableItems();
        }
    }

    removeRow(row: TableItemsPromotion) {
        //this.log('removeRow', row);
        const foundItem = this.itemsBySku[row.sku];

        if(foundItem) {
            switch(this.currentSection()) {
                case 'required-items':
                    this.removeRequiredItem(foundItem);
                case 'items':
                    this.removeItem(foundItem);
            }
        }

        this.applyStatusSource();
    }

    removeRequiredItem(item: ItemsEstended) {
        //this.log('removeRequiredItem', item);
        item._required = false;
        this.requiredItems = this.requiredItems.filter(requiredItem => requiredItem.sku !== item.sku);
        this.requiredItemsSourceRemoved = this.requiredItems;
        this.updateAvailableReqItems();
    }

    removeItem(item: ItemsEstended) {
        //this.log('removeItem', item);
        item._enabled = false;
        this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.sku !== item.sku);
        this.itemsSourceRemoved = this.selectedItems;
        this.updateAvailableItems();
    }

    updateDiscountPrice(sku: string, discountPrice: number) {
        //this.log('updateDiscountPrice', sku, discountPrice);
        const foundItem = this.selectedItems.find((item: ItemsEstended) => item.sku === sku);

        if(foundItem) {
            foundItem._discountedPrice = discountPrice;
        }
    }

    updateQuantity(sku: string, quantity: number) {
        //this.log('updateQuantity', sku, quantity);
        const foundItem = this.requiredItems.find((item: ItemsEstended) => item.sku === sku);

        if(foundItem) {
            foundItem._quantity = quantity;
        }
    }

    onePromotionFormatter(promotion: PromotionsFields): PromotionsFe {
        return {
            id: promotion.id || -1,
            status: promotion.active ? this.translate.instant('PROMOTIONS.SHOWCASE.ACTIVE') : this.translate.instant('PROMOTIONS.SHOWCASE.INACTIVE'),
            name: promotion.name,
            items: promotion.items?.length || 0,
            startData: promotion.from_date ? this.tilbyDatePipe.transform(promotion.from_date, 'd/MM/yy') : '-',
            endData: promotion.to_date ? this.tilbyDatePipe.transform(promotion.to_date, 'd/MM/yy') : '-',
            startTime: promotion.start_time ? this.removeSeconds(promotion.start_time) : '-',
            endTime: promotion.end_time ? this.removeSeconds(promotion.end_time) : '-',
            weekDays: promotion.weekdays_period ? this.getWeekDays(promotion.weekdays_period) : '-',
            fidelity_rule: promotion.fidelity_rule ? this.getFidelityRule(promotion.fidelity_rule) : '-',
            customer_type: promotion.customer_type ? promotion.customer_type : '',
        };
    }

    promotionsFormatter(promotions: Array<PromotionsFields>): Array<PromotionsFe> {
        return promotions.map(promotion => this.onePromotionFormatter(promotion));
    }

    private getWeekDays(weekdays_period: Array<ChainPromotions.WeekdaysPeriodEnum>) {
        const len = weekdays_period?.length || 0;
        return len === 0 || len === 7
            ? this.translate.instant('PROMOTIONS.SHOWCASE.EVERY_DAY')
            : weekdays_period.map(day => this.weekDaysMap[day]).join(', ');
    }

    private getFidelityRule(rule: ChainPromotions.FidelityRuleEnum) {
        switch (rule) {
            case 'none': return this.translate.instant('PROMOTIONS.SHOWCASE.EVERY_CUSTOMER');
            case 'only_fidelity': return this.translate.instant('PROMOTIONS.SHOWCASE.ONLY_FIDELITY');
            case 'no_fidelity': return this.translate.instant('PROMOTIONS.SHOWCASE.NO_FIDELITY');
        }
    }

    private removeSeconds(time: string): string {
        const arr = time.split(':');
        return `${arr[0]}:${arr[1]}`;
    }

    private getNumbersOfWeekdays(weekdays: Array<ChainPromotions.WeekdaysPeriodEnum>): Array<number> {
        return (weekdays && weekdays.length > 0)
            ? weekdays.map((weekday: ChainPromotions.WeekdaysPeriodEnum) => {
                switch (weekday) {
                    case 'Su': return 0;
                    case 'Mo': return 1;
                    case 'Tu': return 2;
                    case 'We': return 3;
                    case 'Th': return 4;
                    case 'Fr': return 5;
                    case 'Sa': return 6;
                }
            })
            : [];
    }

    get from_date() {
        return this.promotionForm?.controls.from_date;
    }
    get to_date() {
        return this.promotionForm?.controls.to_date;
    }

    get perc_discount() {
        return this.promotionForm?.controls.perc_discount;
    }

    private log(...args: any[]) {
        DevLogger.log('UTILS_PROMOTIONS_SERVICE', ...args);
    }
}
